import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export const THREAD_PARAM = 'thread';

/** Keeps the open diagnosis thread in the URL, so a chat can be shared.
 *
 *  A thread is the most valuable artefact this tool produces — a colleague
 *  asked the question, the model read the evidence, and the answer cites it.
 *  Until now that lived in React state only: the URL named the booking, never
 *  the conversation, so "look at what it found" meant re-running it.
 *
 *  Unlike `useCreateIntent` / `useEditIntent`, the param is NOT consumed on
 *  read. It has to keep describing the open thread, or the Share button would
 *  have nothing to copy a moment later. It is written with history `replace`
 *  and by merging into the existing params, so it neither adds back-button
 *  steps nor fights `TenantScopeUrlSync`, which stamps `?scope` into every
 *  URL the same way.
 */
export function useThreadParam(anchorId: string | null, adopt: (id: string) => void) {
  const [params, setParams] = useSearchParams();
  const urlThread = params.get(THREAD_PARAM);

  // URL -> state: a link opened in a fresh tab, or a back/forward step.
  useEffect(() => {
    if (urlThread && urlThread !== anchorId) adopt(urlThread);
    // Keyed on the param alone: re-running when anchorId changes would undo
    // "New thread" the instant it cleared the state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlThread]);

  // state -> URL: starting, opening or clearing a thread.
  useEffect(() => {
    const current = params.get(THREAD_PARAM);
    if ((anchorId ?? null) === current) return;
    const next = new URLSearchParams(params);
    if (anchorId) next.set(THREAD_PARAM, anchorId);
    else next.delete(THREAD_PARAM);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorId]);
}

/** Strips the thread param from a search string — for the hosts that carry
 *  their query string across tab navigation and dialog close, where a
 *  lingering `thread` would follow the operator back to the grid. */
export function withoutThreadParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(THREAD_PARAM);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}
